import React, { FunctionComponent } from 'react';
import { useAtom } from 'jotai';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next'
import { DragDropProvider } from '@dnd-kit/react';
import { useSortable, isSortable } from '@dnd-kit/react/sortable';
import DragHandleIcon from '@mui/icons-material/DragHandle';

import { currentPracticeAtom } from '../../../../lib/practice';

import {
  Box,
  Divider,
  Grid,
  Paper,
} from '@mui/material';
import {
  Switcher,
  Typography
} from '../../../../components/v2/styled';

import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';

// Two-level settings for the picture_and_tags widget:
//   - 5 top-level sections with on/off + order (order lives inside the
//     composite value as `sections_order`)
//   - the `icon_links` section has its own sub-list with on/off + order (order
//     under `picture_and_tags_order`), and three links are module-gated
//     (nutri_mail / ehealth / zorgmail) so their switches use the disabled
//     state when the module is off (goto_links style).
//
// The composite value bundles everything under one key (`picture_and_tags`):
//   { sections: Record<string,boolean>, sections_order: string[], icon_links: Record<string,boolean> }
// The icon-link order lives under `picture_and_tags_order`. This needs no
// changes to the dialog plumbing — buildWidgetsPayload already merges the value
// key + the order key as siblings under `settings`.

export type PictureAndTagsSettingsValue = {
  sections?: Record<string, boolean>;
  sections_order?: string[];
  icon_links?: Record<string, boolean>;
};

type Props = {
  value: PictureAndTagsSettingsValue;
  onChange: (v: PictureAndTagsSettingsValue) => void;
  order: string[];
  onOrderChange: (v: string[]) => void;
};

const SECTION_LABEL_BASE = 'coach.personal_settings.userdetail_widgets.settings.picture_and_tags.sections';
const ICON_LINK_LABEL_BASE = 'coach.personal_settings.userdetail_widgets.settings.picture_and_tags.icon_links';

export const CoachSettingsWidgetPictureAndTags: FunctionComponent<Props> = ({
  value,
  onChange,
  order,
  onOrderChange
}) => {
  const theme = useTheme();
  const {t} = useTranslation(['translations']);
  const [currentPractice] = useAtom(currentPracticeAtom);

  // Defensive defaults so the dialog works before the first save.
  const sections = value?.sections || {};
  const iconLinks = value?.icon_links || {};

  // Icon-link items in (draft) order, falling back to default order. Drift
  // (unknown/missing keys) tolerated via getOrderedPictureAndTagsIconLinks.
  const orderedIconLinks = HelpersClientWidgets.getOrderedPictureAndTagsIconLinks(order, iconLinks, currentPractice);
  const orderedSections = HelpersClientWidgets.getOrderedPictureAndTagsSections(value?.sections_order, sections);

  return <Grid container spacing={2}>
    <Grid item xs={12}>
      <Typography variant="caption" sx={{color: 'text.secondary'}}>
        {t(`${SECTION_LABEL_BASE}.description`)}
      </Typography>
    </Grid>

    {/* Section list: show/hide + reorder. */}
    <Grid item xs={12}>
      <Typography variant="subtitle2" sx={{color: 'text.secondary'}}>
        {t(`${SECTION_LABEL_BASE}.title`)}
      </Typography>
      <Paper variant="outlined" sx={{padding: 1, marginTop: 0.5}}>
        <DragDropProvider
          onDragEnd={(event) => {
            if (event.canceled) return;
            const { source } = event.operation;
            // Same OptimisticSortingPlugin handling as the icon-links list below.
            if (!source || !isSortable(source)) return;
            const from = source.sortable.initialIndex;
            const to = source.sortable.index;
            if (from === to) return;

            const currentOrder = orderedSections.map((i) => i.key);
            const next = [...currentOrder];
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            onChange({ ...value, sections_order: next });
          }}
        >
          {orderedSections.map((item, index) => (
            <SectionSortableRow
              key={item.key}
              id={item.key}
              index={index}
              item={item}
              label={t(`${SECTION_LABEL_BASE}.items.${item.key}`)}
              theme={theme}
              checked={item.state === 'shown'}
              onToggle={(c) => onChange({ ...value, sections: { ...sections, [item.key]: c } })}
            />
          ))}
        </DragDropProvider>
      </Paper>
    </Grid>

    <Grid item xs={12}>
      <Divider />
    </Grid>

    {/* Icon-link sub-list: show/hide + reorder, with disabled state when the
        module is off (mirror of goto_links). */}
    <Grid item xs={12}>
      <Typography variant="subtitle2" sx={{color: 'text.secondary'}}>
        {t(`${ICON_LINK_LABEL_BASE}.title`)}
      </Typography>
      <Paper variant="outlined" sx={{padding: 1, marginTop: 0.5}}>
        <DragDropProvider
          onDragEnd={(event) => {
            if (event.canceled) return;
            const { source } = event.operation;
            // With useSortable, the OptimisticSortingPlugin reorders items by
            // mutating each sortable's `index` during dragover. By dragend the
            // source's new index reflects where it was dropped, so we splice it
            // from its initial index to its new index. We can't rely on
            // event.operation.target here — the plugin re-points it at source.
            if (!source || !isSortable(source)) return;
            const from = source.sortable.initialIndex;
            const to = source.sortable.index;
            if (from === to) return;

            const currentOrder = orderedIconLinks.map((i) => i.key);
            const next = [...currentOrder];
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            onOrderChange(next);
          }}
        >
          {orderedIconLinks.map((item, index) => (
            <IconLinkSortableRow
              key={item.key}
              id={item.key}
              index={index}
              item={item}
              label={t(`${ICON_LINK_LABEL_BASE}.items.${item.key}`)}
              theme={theme}
              checked={item.state === 'active'}
              disabled={item.state === 'disabled'}
              onToggle={(c) => onChange({ ...value, icon_links: { ...iconLinks, [item.key]: c } })}
            />
          ))}
        </DragDropProvider>
      </Paper>
    </Grid>
  </Grid>;
}

type IconLinkRowProps = {
  id: string;
  index: number;
  item: any;
  label: string;
  theme: any;
  checked: boolean;
  disabled: boolean;
  onToggle: (checked: boolean) => void;
};

const IconLinkSortableRow: FunctionComponent<IconLinkRowProps> = ({id, index, item, label, theme, checked, disabled, onToggle}) => {
  const { ref, handleRef, isDragSource, isDropTarget } = useSortable({ id, index });

  const { Icon } = item;
  const stateColor = item.state === 'active'
    ? theme.palette.primary.main
    : item.state === 'inactive'
      ? theme.palette.secondary.main
      : theme.palette.text.disabled;

  return (
    <Box
      ref={ref}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        padding: 1,
        borderRadius: 0.5,
        marginTop: 0.5,
        backgroundColor: isDropTarget ? 'rgba(151,190,107,0.12)' : 'transparent',
        opacity: isDragSource ? 0.5 : 1,
        border: '1px solid',
        borderColor: isDropTarget ? theme.palette.primary.main : 'transparent',
        transition: 'background-color 0.15s ease-in-out, border-color 0.15s ease-in-out'
      }}
    >
      <Box
        ref={handleRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'grab',
          color: theme.palette.text.secondary
        }}
      >
        <DragHandleIcon fontSize="small" />
      </Box>
      <Icon sx={{fontSize: '1.2rem', color: stateColor}} active={checked} />
      <Switcher
        label={label}
        value={checked}
        disabled={disabled}
        greying
        onChange={onToggle}
      />
    </Box>
  );
}

type SectionRowProps = {
  id: string;
  index: number;
  item: any;
  label: string;
  theme: any;
  checked: boolean;
  onToggle: (checked: boolean) => void;
};

const SectionSortableRow: FunctionComponent<SectionRowProps> = ({id, index, item, label, theme, checked, onToggle}) => {
  const { ref, handleRef, isDragSource, isDropTarget } = useSortable({ id, index });

  const { Icon } = item;
  const stateColor = checked
    ? theme.palette.primary.main
    : theme.palette.secondary.main;

  return (
    <Box
      ref={ref}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        padding: 1,
        borderRadius: 0.5,
        marginTop: 0.5,
        backgroundColor: isDropTarget ? 'rgba(151,190,107,0.12)' : 'transparent',
        opacity: isDragSource ? 0.5 : 1,
        border: '1px solid',
        borderColor: isDropTarget ? theme.palette.primary.main : 'transparent',
        transition: 'background-color 0.15s ease-in-out, border-color 0.15s ease-in-out'
      }}
    >
      <Box
        ref={handleRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'grab',
          color: theme.palette.text.secondary
        }}
      >
        <DragHandleIcon fontSize="small" />
      </Box>
      <Icon sx={{fontSize: '1.2rem', color: stateColor}} />
      <Switcher
        label={label}
        value={checked}
        greying
        onChange={onToggle}
      />
    </Box>
  );
}

// Read-only status: two compact blocks. The sections row shows each section's
// icon in saved order, coloured by shown/hidden. The icon-links row shows each
// link's icon in saved order, coloured by state (primary=active, secondary=inactive, grey=disabled).
type StatusProps = {
  value: PictureAndTagsSettingsValue;
  order?: string[];
};

export const CoachSettingsWidgetPictureAndTagsStatus: FunctionComponent<StatusProps> = ({
  value,
  order
}) => {
  const theme = useTheme();
  const {t} = useTranslation(['translations']);
  const [currentPractice] = useAtom(currentPracticeAtom);

  const sections = value?.sections || {};
  const iconLinks = value?.icon_links || {};
  const orderedSections = HelpersClientWidgets.getOrderedPictureAndTagsSections(value?.sections_order, sections);
  const orderedIconLinks = HelpersClientWidgets.getOrderedPictureAndTagsIconLinks(order, iconLinks, currentPractice);

  return <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5, marginTop: 1}}>
    <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5}}>
      {orderedSections.map((item) => {
        const { Icon } = item;
        const color = item.state === 'shown' ? theme.palette.primary.main : theme.palette.secondary.main;
        return <Box
          key={item.key}
          title={t(`${SECTION_LABEL_BASE}.items.${item.key}`)}
        >
          <Icon sx={{ fontSize: '1.1rem', color }} />
        </Box>;
      })}
    </Box>
    <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5}}>
      {orderedIconLinks.map((item) => {
        const { Icon } = item;
        const color = item.state === 'active'
          ? theme.palette.primary.main
          : item.state === 'inactive'
            ? theme.palette.secondary.main
            : theme.palette.text.disabled;
        return <Box
          key={item.key}
          title={t(`${ICON_LINK_LABEL_BASE}.items.${item.key}`)}
        >
          <Icon sx={{ fontSize: '1.1rem', color }} active={item.state === 'active'} />
        </Box>;
      })}
    </Box>
  </Box>;
}
